import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  User,
  Mail,
  Phone,
  Shield,
  LogOut,
  Loader2,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
  });

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await api().patch(`/users/${user.id}`, formData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = (role) => {
    const colors = {
      director: 'bg-purple-50 text-purple-700',
      manager: 'bg-blue-50 text-blue-700',
      staff: 'bg-zinc-100 text-zinc-700',
    };
    return colors[role] || 'bg-zinc-100 text-zinc-700';
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Settings</h1>
        <p className="text-zinc-500 mt-1">Manage your account settings</p>
      </div>

      {/* Profile Card */}
      <Card className="border-zinc-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar and role */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-zinc-900 flex items-center justify-center text-white text-2xl font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="text-lg font-medium text-zinc-900">{user?.full_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getRoleBadge(user?.role)}>
                  {user?.role}
                </Badge>
                {user?.is_admin && (
                  <Badge className="bg-amber-50 text-amber-700">
                    <Shield className="mr-1 h-3 w-3" />
                    Admin
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Email (read-only) */}
          <div>
            <Label className="flex items-center gap-2 text-zinc-500">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            <p className="mt-1 text-zinc-900">{user?.email}</p>
          </div>

          {/* Editable fields */}
          <div>
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="mt-2"
              data-testid="settings-name-input"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="0400 000 000"
              className="mt-2"
              data-testid="settings-phone-input"
            />
          </div>

          <Button
            onClick={handleUpdateProfile}
            disabled={loading}
            className="w-full"
            data-testid="save-profile-btn"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card className="border-zinc-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/reset-password')}
            data-testid="change-password-btn"
          >
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <Button
            variant="outline"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
